import { Component, ComponentInterface, Element, Event, EventEmitter, Prop, QueueApi, State, Watch, Host, h } from '@stencil/core';

import { CheckedInputChangeEvent, Color, Gesture, GestureDetail } from '../../../interface';
import { renderHiddenInput } from '../../../utils/helpers';
import { createColorClasses } from '../../../utils/theme';
import { createGesture } from '../../../utils/gesture';
@Component({
  tag: 'lar-toggle',
  styleUrl: 'toggle.scss',
  shadow: true
})
export class Toggle implements ComponentInterface {

  private inputId = `lar-tg-${toggleIds++}`;
  private pivotX = 0;
  private gesture?: Gesture;

  @Element() el!: HTMLElement;

  @State() activated = false;
  @State() keyFocus = false;

  /**
   * The color to use from your application's color palette.
   * Default options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop() color?: Color;

  /**
   * The name of the control, which is submitted with the form data.
   */
  @Prop() name: string = this.inputId;

  /**
   * If `true`, the toggle is selected. Defaults to `false`.
   */
  @Prop({ mutable: true }) checked = false;

  /**
   * If `true`, the user cannot interact with the toggle. Defaults to `false`.
   */
  @Prop({ mutable: true }) disabled = false;

  /**
   * The value of the toggle does not mean if it's checked or not, use the `checked`
   * property for that.
   *
   * The value of a toggle is analogous to the value of a `<input type="checkbox">`,
   * it's only used when the toggle participates in a native `<form>`.
   * Defaults to `on`.
   */
  @Prop() value?: string | null = 'on';

  /**
   * Emitted when the value property has changed.
   */
  @Event() larchange!: EventEmitter<CheckedInputChangeEvent>;

  /**
   * Emitted when the toggle has focus.
   */
  @Event() larfocus!: EventEmitter<void>;

  /**
   * Emitted when the toggle loses focus.
   */
  @Event() larblur!: EventEmitter<void>;

  @Watch('checked')
  checkedChanged(isChecked: boolean) {
    this.larchange.emit({
      checked: isChecked,
      value: this.value
    });
  }

  @Watch('disabled')
  async disabledChanged() {
    try {
      this.gesture?.enable(!Boolean(this.disabled));
    } catch (err) {
      console.error(err);
    }
  }

  async componentDidLoad() {
    try {
      this.gesture = createGesture({
        el: this.el,
        gestureName: 'toggle',
        gesturePriority: 100,
        threshold: 0,
        onStart: ev => this.onStart(ev),
        onMove: ev => this.onMove(ev),
        onEnd: ev => this.onEnd(ev),
      });
      this.disabledChanged();
    } catch (err) {
      console.error(err);
    }
  }

  private onStart(detail: GestureDetail) {
    this.pivotX = detail.currentX;
    this.activated = true;

    // touch-action does not work in iOS
    detail.event?.preventDefault();
    detail.event?.stopPropagation();
    return true;
  }

  private onMove(detail: GestureDetail) {
    const currentX = detail.currentX;
    if (shouldToggle(this.checked, currentX - this.pivotX, -5)) {
      this.checked = !this.checked;
      this.pivotX = currentX;
    }
    detail.event?.preventDefault();
    detail.event?.stopPropagation();
  }

  private onEnd(detail: GestureDetail) {
    const delta = detail.currentX - this.pivotX;
    if (shouldToggle(this.checked, delta, 0)) {
      this.checked = !this.checked;
    }
    this.activated = false;
    detail.event?.preventDefault();
    detail.event?.stopPropagation();
  }

  private onChange = (event: CustomEvent) => {
    this.checked = !this.checked;
    event.stopPropagation();
    event.preventDefault();
  }

  private onKeyUp = () => {
    this.keyFocus = true;
  }

  private onFocus = (event: FocusEvent) => {
    this.larfocus.emit();
    event.stopPropagation();
    event.preventDefault();
  }

  private onBlur = (event: FocusEvent) => {
    this.keyFocus = false;
    this.larblur.emit();
    event.stopPropagation();
    event.preventDefault();
  }

  private getValue() {
    return this.value || '';
  }

  render() {
    const value = this.getValue();
    renderHiddenInput(this.el, this.name, (this.checked ? value : ''), this.disabled);

    return (
      <Host
        class={{
          ...createColorClasses(this.color),
          'lar-toggle-activated': this.activated,
          'lar-toggle-checked': this.checked,
          'lar-toggle-disabled': this.disabled,
          'lar-toggle-key': this.keyFocus
         }}
      >
        <div class="lar-toggle-icon">
          <div class="lar-toggle-inner"/>
        </div>
        <input
          type="checkbox"
          onChange={this.onChange}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          onKeyUp={this.onKeyUp}
          checked={this.checked}
          id={this.inputId}
          name={this.name}
          value={value}
          disabled={this.disabled}
        />
        <slot></slot>
      </Host>
    );
  }
}

function shouldToggle(checked: boolean, deltaX: number, margin: number): boolean {
  if (checked) {
    return (margin >= deltaX);
  } else {
    return (- margin <= deltaX);
  }
}

let toggleIds = 0;
